/* import __COLOCATED_TEMPLATE__ from './hubspot.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import type AppService from 'embercom/services/app-service';
import { get } from 'embercom/lib/ajax';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import { SyncBehaviorType, SourceType } from 'embercom/models/import-service/import-source';
import type ImportSource from 'embercom/models/import-service/import-source';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator } from 'ember-concurrency';
import { task } from 'ember-concurrency-decorators';

interface HubSpotKnowledgeBase {
  id: string;
  name: string;
  language: string;
}

interface Signature {
  Args: {
    modal: any;
    title?: string;
    onNavigationBack?: () => void;
    onNavigationNext?: (viewId?: string) => void;
    showBackButton?: boolean;
    onModalClose: () => void;
  };
  Element: null;
}

export default class HubspotSyncModal extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare intercomEventService: $TSFixMe;
  @service declare knowledgeHubService: KnowledgeHubService;

  @tracked selectedKnowledgeBase: HubSpotKnowledgeBase | null = null;
  @tracked knowledgeBases: HubSpotKnowledgeBase[] = [];

  private syncBehavior: SyncBehaviorType = SyncBehaviorType.Sync;
  private importSource: ImportSource | undefined;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    taskFor(this.loadKnowledgeBases).perform();
  }

  // The backend requires an import_source_id to look up HubSpot credentials,
  // so we create one first and use it to fetch the available knowledge bases.
  @task({ drop: true })
  *loadKnowledgeBases(): TaskGenerator<void> {
    try {
      this.importSource = yield this.knowledgeHubService.createImportSource({
        sourceType: SourceType.HubSpot,
        syncBehavior: this.syncBehavior,
      });

      if (!this.importSource) {
        return;
      }

      let response: { knowledge_bases: HubSpotKnowledgeBase[] } = yield get(
        '/ember/import_service/import_sources/hubspot/knowledge_bases',
        {
          app_id: this.appService.app.id,
          import_source_id: this.importSource.id,
        },
      );

      this.knowledgeBases = response.knowledge_bases;
    } catch (error: unknown) {
      console.error('Failed to load HubSpot knowledge bases:', error);
    }
  }

  get isLoading() {
    return taskFor(this.loadKnowledgeBases).isRunning;
  }

  get knowledgeBaseOptions() {
    return this.knowledgeBases.map((kb: HubSpotKnowledgeBase) => {
      return {
        text: kb.name,
        value: kb.id,
      };
    });
  }

  get syncButtonDisabled() {
    return !this.selectedKnowledgeBase || this.isLoading;
  }

  @action
  switchKnowledgeBase(value: string) {
    this.selectedKnowledgeBase =
      this.knowledgeBases.find((kb: HubSpotKnowledgeBase) => kb.id === value) || null;
  }

  @action
  async startSync() {
    if (!this.selectedKnowledgeBase || !this.importSource) {
      return;
    }

    try {
      // Targeted PUT avoids Fragment serialization overwriting context with defaults
      await this.knowledgeHubService.updateImportSourceWithParam(this.importSource.id, {
        knowledge_base_id: this.selectedKnowledgeBase.id,
      });

      await this.knowledgeHubService.startImport(this.importSource.id, this.syncBehavior);

      let notificationMessage =
        this.syncBehavior === SyncBehaviorType.Sync
          ? this.intl.t('knowledge-hub.sync-modal.hubspot.sync-started')
          : this.intl.t('knowledge-hub.sync-modal.hubspot.import-started');

      this.knowledgeHubService.notifySourceStateChangeConfirmation(
        notificationMessage,
        this.importSource.id,
      );

      this.intercomEventService.trackAnalyticsEvent({
        action: 'clicked',
        context: 'import-content',
        object: 'import_content_hubspot',
        section: 'knowledge-hub.new-content.hubspot',
        place: 'knowledge-hub.new-content',
      });

      this.args.onModalClose();
    } catch (error: unknown) {
      console.error('Failed to start HubSpot import:', error);
    }
  }

  @action
  async cancelSync() {
    taskFor(this.loadKnowledgeBases).cancelAll();
    try {
      if (this.importSource) {
        await this.importSource.remove({ notify: false });
      }
    } catch {
      // Best-effort cleanup
    }
    this.args.onModalClose();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::SetupModal::SyncModal::Hubspot': typeof HubspotSyncModal;
    'knowledge-hub/setup-modal/sync-modal/hubspot': typeof HubspotSyncModal;
  }
}
